import { ArrowBigUpDash, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../components/tooltip';
import { cn } from '../lib/utils';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
    leftSlot?: React.ReactNode;
    rightSlot?: React.ReactNode;
    inputClassName?: string;
};

function Input({ ref, leftSlot, rightSlot, className, inputClassName, type, placeholder, ...props }: InputProps & { ref?: React.RefObject<HTMLInputElement | null> }) {
    const { t } = useTranslation('common');

    const [capsLockActive, setCapsLockActive] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleKeyPress: React.KeyboardEventHandler<HTMLInputElement> = (
        event,
    ) => {
        const capsLockOn = event.getModifierState('CapsLock');
        setCapsLockActive(capsLockOn);
    };

    const togglePasswordVisibility = () => setShowPassword(!showPassword);

    const inputClasses = cn(
        // 'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        // Layout
        'flex h-10 w-full items-center justify-between gap-2 rounded-md',
        // Background & Border
        'border border-border bg-transparent border-box',
        // Text
        'px-3 py-2 text-sm',
        // Transition
        'transition duration-100',
        // File input specific
        'file:border-0 file:bg-transparent file:text-sm file:font-medium',
        // Placeholder & Hover
        'placeholder:text-text-placeholder placeholder:font-normal hover:border-input-border-hover hover:shadow-input-hover',
        // Focus
        'focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        // Disabled
        'disabled:cursor-not-allowed disabled:opacity-50',
        leftSlot && 'pl-8',
        type === 'password' && (!capsLockActive ? 'pr-8' : 'pr-16'),
        inputClassName,
    );

    return (
        <div className={cn('relative flex', className)}>
            {leftSlot && (
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                    {leftSlot}
                </div>
            )}
            <input
                type={type === 'password' && showPassword ? 'text' : type}
                className={inputClasses}
                onKeyDown={handleKeyPress}
                ref={ref}
                placeholder={placeholder}
                {...props}
                style={{
                    ...props.style,
                    WebkitAppearance: 'none',
                    MozAppearance: 'textfield',
                }}
            />
            {type === 'password' && (
                <div className="absolute right-0 top-1/2 flex -translate-y-1/2 items-center gap-x-1 pr-3">
                    {showPassword
                        ? (
                                <EyeOffIcon
                                    className="cursor-pointer"
                                    onClick={togglePasswordVisibility}
                                    size={20}
                                />
                            )
                        : (
                                <EyeIcon
                                    className="cursor-pointer"
                                    onClick={togglePasswordVisibility}
                                    size={20}
                                />
                            )}
                    {capsLockActive && type === 'password' && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <ArrowBigUpDash size={20} />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>
                                        {t(
                                            'components.input.capsLockActive',
                                        )}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    {rightSlot}
                </div>
            )}
            {type !== 'password' && rightSlot && (
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    {rightSlot}
                </div>
            )}
        </div>
    );
}
Input.displayName = 'Input';

/**
 * InputGroupControl - A minimal input component for use within InputGroup
 * This component has no border, shadow, ring, or hover/focus effects
 * as the InputGroup container handles all visual styling
 */
function InputGroupControl({ ref, className, ...props }: InputProps & { ref?: React.RefObject<HTMLInputElement | null> }) {
    const inputClasses = cn(
        // Layout
        'flex h-auto w-full items-center',
        // Background & Border - None
        'border-none bg-transparent shadow-none',
        // Text
        'px-3 py-2 text-sm',
        // File input specific
        'file:border-0 file:bg-transparent file:text-sm file:font-medium',
        // Placeholder
        'placeholder:text-text-placeholder placeholder:font-normal',
        // Focus - No effects
        'focus:border-none focus:shadow-none focus:ring-none focus:outline-none',
        'focus-visible:border-none focus-visible:shadow-none focus-visible:ring-0 focus-visible:outline-none',
        // Hover - No effects
        'hover:border-none hover:shadow-none',
        // Disabled
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
    );

    return (
        <input
            type={props.type}
            className={inputClasses}
            ref={ref}
            {...props}
            style={{
                ...props.style,
                WebkitAppearance: 'none',
                MozAppearance: 'textfield',
            }}
        />
    );
}
InputGroupControl.displayName = 'InputGroupControl';

export { Input, InputGroupControl };
